import React, { useEffect, useRef } from "react";
import { Toast } from "@base-ui/react/toast";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";

/**
 * Pram Express toasts — drop-in replacement for react-toastify, built on
 * Base UI's Toast (unstyled) and themed with the px design tokens.
 *
 * Keeps the exact API the app already uses:
 *   <ToastContainer />            (render once per page)
 *   toast.success(msg) / error / warning / warn / info(msg, opts?)
 *
 * The imperative `toast` object bridges to the mounted Toast.Provider via a
 * module-level manager set by <Bridge/> when the container mounts.
 */

let addToast = null;

function push(type, msg, opts = {}) {
    if (!addToast) return;
    addToast({
        title: typeof msg === 'string' ? msg : msg ? String(msg) : '',
        description: (opts && opts.description) || '',
        type,
        timeout: (opts && opts.timeout) || 3500,
        id: opts && opts.id,
    });
}

export const toast = {
    success: (msg, opts) => push('success', msg, opts),
    error: (msg, opts) => push('error', msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    warn: (msg, opts) => push('warning', msg, opts), // react-toastify alias
    info: (msg, opts) => push('info', msg, opts),
};

const icons = {
    success: <CheckCircle2 size={18} />,
    error: <XCircle size={18} />,
    warning: <AlertTriangle size={18} />,
    info: <Info size={18} />,
};

function ToastList() {
    const { toasts } = Toast.useToastManager();
    return toasts.map((t) => (
        <Toast.Root key={t.id} toast={t} className="px-toast-root">
            <Toast.Content className="px-toast">
                <span className={`px-toast__icon px-toast__icon--${t.type || 'info'}`}>
                    {icons[t.type] || icons.info}
                </span>
                <div className="px-toast__body">
                    <Toast.Title className="px-toast__title" />
                    <Toast.Description className="px-toast__desc" />
                </div>
                <Toast.Close className="px-toast__close" aria-label="Dismiss">
                    <X size={15} />
                </Toast.Close>
            </Toast.Content>
        </Toast.Root>
    ));
}

function Bridge() {
    const manager = Toast.useToastManager();
    useEffect(() => {
        addToast = (data) => manager.add(data);
        return () => { addToast = null; };
    }, [manager]);
    return null;
}

export function ToastContainer({ children }) {
    return (
        <Toast.Provider>
            <Bridge />
            {children}
            <Toast.Portal>
                <Toast.Viewport className="px-toast-viewport">
                    <ToastList />
                </Toast.Viewport>
            </Toast.Portal>
        </Toast.Provider>
    );
}

export default ToastContainer;
